import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { subscribeToSubscriptionRestrictions } from '../../shared/subscriptionRestriction';
import { subscriptionGateway } from './index';
import { onSubscriptionChanged } from './subscriptionChanged';
import type { CompanySubscriptionResponse } from './types';

/** EPN-79 — a small, shared "what's this company's subscription/trial state right now" hook, so trial
 * visibility (the topbar badge, dashboard card, etc.) doesn't need its own independent fetch logic in every
 * consumer. Deliberately NOT folded into `IdentityContext`/`MeResponse`: that's an Identity.Api-owned
 * contract, and subscription state is intentionally Subscriptions.Api's own concern (see
 * docs/subscriptions-architecture.md's module-boundary note) — this hook is the frontend-side equivalent of
 * that boundary, not a workaround for it.
 *
 * `error` is true when the state could not be determined — distinct from "no subscription" (`subscription`
 * null, `error` false). Consumers must not guess in that case: a failed lookup is neither "paid" nor
 * "expired" (approved Trial Dashboard State SRS §53/§54). */
export function useCurrentSubscription(): {
  subscription: CompanySubscriptionResponse | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const [subscription, setSubscription] = useState<CompanySubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // AppShell (and this hook's consumers within it, e.g. TrialUpgradeBadge) persists across route changes —
  // it is never remounted just because the user navigates from, say, choosing a plan to the confirmation
  // page. Re-running the fetch on every pathname change is what makes the topbar badge reflect a trial
  // that started moments ago on a different screen, not just whatever was true when AppShell first mounted.
  const { pathname } = useLocation();

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(false);
    subscriptionGateway
      .getCurrentSubscription(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setSubscription(result);
      })
      .catch(() => {
        // Never guess a state on failure: surface "could not verify" and keep whatever was known out of it.
        if (!controller.signal.aborted) {
          setSubscription(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [pathname, reloadToken]);

  // A billing action elsewhere changed the subscription, or the server just refused a write because of the
  // subscription state (which may mean the state moved while this page was open) — re-read the server's answer.
  useEffect(() => {
    const refetch = () => setReloadToken((n) => n + 1);
    const offChanged = onSubscriptionChanged(refetch);
    const offRestriction = subscribeToSubscriptionRestrictions(refetch);
    return () => {
      offChanged();
      offRestriction();
    };
  }, []);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  return { subscription, loading, error, reload };
}
